/**
 * This module provides a function to generate a PDF overview of sessions for a client.
 * The PDF includes session details such as date, time, location, duration, and objectives, formatted in a clean and professional layout.
 * Currently it is hardcoded for Mirjam Berle, but it could be adapted for other clients in the future if needed.
 * TODO: remove hardcoding of Mirjam Berle and make it more generic for other clients please - best regards Gabe :)
 */

import jsPDF from "jspdf";

export type PdfSessionInput = {
  startTime: Date | string;
  endTime: Date | string;
  location?: string;
  name: string;
  objective?: string;
};

export type PdfClientInput = {
  name: string;
};

const pad2 = (n: number) => String(n).padStart(2, "0");

const toDate = (value: Date | string): Date | null => {
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateDe = (date: Date) =>
  `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}`;

const formatTimeHm = (date: Date) =>
  `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

const computeDuration = (start: Date, end: Date) => {
  let mins = Math.round((end.getTime() - start.getTime()) / 60000);
  if (mins < 0) mins = 0;
  return `${pad2(Math.floor(mins / 60))}:${pad2(mins % 60)}`;
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const LOGO_URL = `${import.meta.env.BASE_URL}mirjam-berle-logo.png`;

export async function generateSessionOverviewPdf(
  client: PdfClientInput,
  sessions: PdfSessionInput[],
  mode: "download" | "show" = "download",
) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;

  let logoImg: HTMLImageElement | null = null;
  try {
    logoImg = await loadImage(LOGO_URL);
  } catch {
    logoImg = null;
  }

  const logoMaxHeight = 18;
  const logoMaxWidth = 45;
  let logoW = 0;
  let logoH = 0;
  if (logoImg && logoImg.width > 0 && logoImg.height > 0) {
    const ratio = logoImg.width / logoImg.height;
    logoH = logoMaxHeight;
    logoW = logoH * ratio;
    if (logoW > logoMaxWidth) {
      logoW = logoMaxWidth;
      logoH = logoW / ratio;
    }
  }

  const sorted = [...sessions]
    .map((s) => ({
      ...s,
      _start: toDate(s.startTime),
      _end: toDate(s.endTime),
    }))
    .filter((s) => s._start !== null)
    .sort((a, b) => (a._start as Date).getTime() - (b._start as Date).getTime());

  const today = formatDateDe(new Date());
  const headerTitle = `Mirjam Berle - Übersicht für ${client.name}`;
  const createdLine = `Diese Übersicht wurde erstellt am: ${today}`;

  const headerHeight = 30;
  const topContentY = margin + headerHeight + 5;
  const footerY = pageHeight - 10;

  const drawHeader = () => {
    if (logoImg && logoW > 0) {
      doc.addImage(logoImg, "PNG", pageWidth - margin - logoW, margin, logoW, logoH);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(36, 44, 99);
    const titleMaxWidth = contentWidth - (logoW > 0 ? logoW + 5 : 0);
    doc.text(headerTitle, margin, margin + 6, { maxWidth: titleMaxWidth });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(createdLine, margin, margin + 14);

    doc.setDrawColor(223, 225, 227);
    doc.setLineWidth(0.3);
    doc.line(margin, margin + headerHeight, pageWidth - margin, margin + headerHeight);
  };

  const drawFooter = (pageNum: number, totalPages: number) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(`Seite ${pageNum} von ${totalPages}`, pageWidth - margin, footerY, {
      align: "right",
    });
  };

  let cursorY = topContentY;
  drawHeader();

  const containerPadding = 4;
  const lineHeight = 5;
  const objectiveTopGap = 2.5;

  for (const s of sorted) {
    const start = s._start as Date;
    const end = s._end ?? start;
    const dateStr = formatDateDe(start);
    const startStr = formatTimeHm(start);
    const endStr = formatTimeHm(end);
    const duration = computeDuration(start, end);
    const location = s.location?.trim();
    const timeRange = `${startStr} - ${endStr} Uhr`;
    const metaLine = [
      dateStr,
      timeRange,
      location,
      `Dauer: ${duration}`,
    ]
      .filter(Boolean)
      .join("   •   ");
    const titleLine = s.name;

    const objectiveRaw = s.objective?.replace(/\s+/g, " ").trim();
    let objectiveLine = "";
    if (objectiveRaw) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const maxWidth = contentWidth - containerPadding * 2;
      const lines = doc.splitTextToSize(objectiveRaw, maxWidth) as string[];
      const first = lines[0] ?? "";
      objectiveLine =
        lines.length > 1 ? `${first.replace(/\s+\S*$/, "")}…` : first;
    }

    const blockHeight =
      containerPadding +
      lineHeight +
      lineHeight +
      (objectiveLine ? lineHeight + objectiveTopGap : 0) +
      containerPadding;

    if (cursorY + blockHeight > footerY - 8) {
      doc.addPage();
      drawHeader();
      cursorY = topContentY;
    }

    doc.setDrawColor(223, 225, 227);
    doc.setFillColor(250, 250, 251);
    doc.setLineWidth(0.3);
    doc.roundedRect(margin, cursorY, contentWidth, blockHeight, 2, 2, "FD");

    let textY = cursorY + containerPadding + 4;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(93, 64, 55);
    doc.text(metaLine, margin + containerPadding, textY);
    textY += lineHeight;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(36, 44, 99);
    doc.text(titleLine, margin + containerPadding, textY);

    if (objectiveLine) {
      textY += lineHeight + objectiveTopGap;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(objectiveLine, margin + containerPadding, textY);
    }

    cursorY += blockHeight + 4;
  }

  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(i, totalPages);
  }

  const safeName = client.name.replace(/\s+/g, "_");
  if (mode === "show") {
    window.open(doc.output("bloburl"), "_blank");
  } else {
    doc.save(`Sessions_Uebersicht_${safeName}.pdf`);
  }
}
